import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const TONE = {
  high: { bg: 'bg-emerald-500/10', border: 'border-emerald-300', text: 'text-emerald-700 dark:text-emerald-400' },
  medium: { bg: 'bg-amber-500/10', border: 'border-amber-300', text: 'text-amber-700 dark:text-amber-400' },
  low: { bg: 'bg-stone-500/10', border: 'border-stone-300', text: 'text-stone-700 dark:text-stone-400' },
};

/**
 * Inline Top-3 candidate picker for unmatched bank-statement rows.
 * Calls /api/bank-matching/suggest with the row context, then renders the
 * top-3 candidates ordered by confidence with a one-click "Use this" action.
 */
export default function MatchCandidatesPicker({ row, onPick, testidPrefix = 'cand' }) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState(null);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const r = await api.post('/bank-matching/suggest', {
        row: {
          description: row.description || row.txn_desc || '',
          amount: row.amount ?? row.txn_amount,
          iban: row.iban,
          account_number: row.account_number,
          party_name: row.party_name || row.payee || row.payer,
        },
        top_k: 3,
      });
      // Fingerprint memory: when this exact pattern has been picked ≥3 times in
      // a row to the same entity, the picker auto-applies it instead of asking.
      const hint = r.data?.auto_confirm_hint;
      const cands = r.data?.candidates || [];
      if (hint && hint.entity_id) {
        const locked = cands.find((c) => (c.id || c.entity_id) === hint.entity_id) || {
          id: hint.entity_id,
          entity_type: hint.entity_type,
          name: 'Auto-confirmed',
          score: 95,
          confidence: 'high',
          match_reason: `pattern memorized (${hint.consecutive_picks}× in a row)`,
        };
        toast.success(`Auto-confirmed using pattern memory (${hint.consecutive_picks}× in a row)`, { duration: 4500 });
        onPick(locked);
        return;
      }
      setCandidates(cands);
      if (!cands.length) toast.info('No high-confidence candidates found');
    } catch (e) { toast.error(e.response?.data?.detail || 'Suggest failed'); }
    finally { setLoading(false); }
  };

  // Wrap onPick so the parent can call us, but we ALSO record the pick in
  // fingerprint memory before delegating so future similar txns auto-confirm.
  const handlePick = async (c) => {
    try {
      await api.post('/bank-matching/record-pick', {
        row: {
          description: row.description || row.txn_desc || '',
          amount: row.amount ?? row.txn_amount,
          iban: row.iban,
          account_number: row.account_number,
          party_name: row.party_name || row.payee || row.payer,
        },
        entity_type: c.entity_type,
        entity_id: c.id || c.entity_id,
      });
    } catch (_) {}
    onPick(c);
  };

  if (!candidates) {
    return (
      <Button size="sm" variant="ghost" onClick={fetchCandidates} disabled={loading} data-testid={`${testidPrefix}-fetch`} className="h-6 text-[10px] text-violet-600 hover:bg-violet-50">
        {loading ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Sparkles size={11} className="mr-1" />}
        Top-3
      </Button>
    );
  }

  if (candidates.length === 0) {
    return <span className="text-[10px] text-muted-foreground italic">no candidates</span>;
  }

  return (
    <div className="flex gap-1 flex-wrap" data-testid={`${testidPrefix}-list`}>
      {candidates.slice(0, 3).map((c, i) => {
        const t = TONE[c.confidence] || TONE.low;
        return (
          <button
            key={i}
            onClick={() => handlePick(c)}
            className={`text-[10px] px-1.5 py-1 rounded border ${t.bg} ${t.border} ${t.text} hover:scale-[1.02] transition-transform max-w-[180px] text-left`}
            data-testid={`${testidPrefix}-${i}`}
            title={`${c.confidence} match · score ${c.score?.toFixed?.(2) || c.score} · ${c.entity_type}`}
          >
            <div className="font-bold truncate">{c.score ? `${Math.round(c.score)}%` : ''} {c.name || c.entity_name || c.id}</div>
            <div className="opacity-70 capitalize text-[9px]">{c.entity_type} · {c.match_reason || c.reason || c.confidence}</div>
          </button>
        );
      })}
    </div>
  );
}
